// @rpg-unit/save-policy-presets: when a game saves (tier 3; composition framework, decision 0077). Three policies as
// data, by genre: `checkpoint` (spike 002's, cut from `src/save/index.ts`'s autosave triggers and its tuning
// `save.autosaveMinInterval` on 2026-10-20: autosave at zone changes and on quitting, three manual slots from pause),
// `day-end` (a farm's: saved when the player sleeps, one save, no saving mid-day) and `save-points` (a JRPG's: manual
// slots only at a save point, a quiet autosave at zone changes). The save-slots feature (@rpg-unit/save-slots) runs
// whichever the game picks.
//
// A trigger is a flow transition (`{ from, to, op }`, each field given must match) or a named event (the game emits
// it: `save:checkpoint`, `save:day-end`, `save:point`), with a reason and whether it writes inside the minimum gap.
// Names are string ids (`savePolicy.checkpoint`); `SAVE_POLICY_STRINGS` holds the English defaults.
import table from "./content/tables/save-policies.json" with { type: "json" };
import schema from "./content/schemas/save-policies.schema.json" with { type: "json" };
import strings from "./strings.json" with { type: "json" };

/** This unit's version (the manifest's and package.json's). */
export const SAVE_POLICY_PRESETS_VERSION = "0.1.0" as const;

/** A flow transition as a game's flow reports it. */
export interface FlowTransition {
  op: string;
  from: string | null;
  to: string | null;
}

/** One autosave trigger. */
export interface SaveTrigger {
  reason: string;
  flow?: { op?: string; from?: string; to?: string };
  event?: string;
  force?: boolean;
}

/** One policy, as its schema describes it (`src/content/schemas/save-policies.schema.json`). */
export interface SavePolicy {
  id: string;
  name: { table: string; id: string };
  genres: string[];
  /** Manual slots (`slot-1` … `slot-N`); 0 for autosave only. */
  slots: number;
  /** Where the player writes a manual slot. */
  manual: "pause" | "save-point" | "none";
  /** Unforced autosaves closer together than this are skipped. */
  minIntervalS: number;
  triggers: SaveTrigger[];
  reason: string;
}

/** The policies as a content table. */
export const SAVE_POLICIES_TABLE = table as { table: "save-policies"; version: number; rows: SavePolicy[] };
/** The table's row schema. */
export const SAVE_POLICIES_SCHEMA = schema as Record<string, unknown>;
/** The content this unit adds to a game, in @rpg/content's shape. */
export const SAVE_POLICY_CONTENT = {
  package: "@rpg-unit/save-policy-presets",
  version: SAVE_POLICY_PRESETS_VERSION,
  tables: [SAVE_POLICIES_TABLE],
  schemas: { "save-policies": SAVE_POLICIES_SCHEMA },
} as const;
/** English defaults for the policies' names. */
export const SAVE_POLICY_STRINGS = strings as { id: string; text: string }[];

/** A policy by id (a copy); throws for an unknown one. */
export function savePolicy(id: string, rows: readonly SavePolicy[] = SAVE_POLICIES_TABLE.rows): SavePolicy {
  const p = rows.find((r) => r.id === id);
  if (!p) throw new Error(`save policies: no policy "${id}" (have ${rows.map((r) => r.id).join(", ")})`);
  return structuredClone(p);
}

/** The trigger a flow transition fires under a policy (the first that matches), or null. */
export function flowTrigger(policy: SavePolicy, e: FlowTransition): SaveTrigger | null {
  for (const t of policy.triggers) {
    const f = t.flow;
    if (!f) continue;
    if ((f.op === undefined || f.op === e.op) && (f.from === undefined || f.from === e.from) && (f.to === undefined || f.to === e.to)) return t;
  }
  return null;
}

/** The named events a policy saves on. */
export function eventTriggers(policy: SavePolicy): SaveTrigger[] {
  return policy.triggers.filter((t) => t.event !== undefined);
}

/** A policy's problems beyond its schema (empty when fine): each trigger is a flow or an event, not both or neither;
 *  a manual mode other than "none" needs slots; a flow trigger names at least one field. */
export function checkPolicy(p: SavePolicy): string[] {
  const out: string[] = [];
  for (const [i, t] of p.triggers.entries()) {
    if (!!t.flow === !!t.event) out.push(`${p.id}: trigger ${i} (${t.reason}) must be a flow transition or an event, not ${t.flow ? "both" : "neither"}`);
    if (t.flow && Object.keys(t.flow).length === 0) out.push(`${p.id}: trigger ${i} (${t.reason}) matches every transition`);
  }
  if (p.manual !== "none" && p.slots < 1) out.push(`${p.id}: manual saves "${p.manual}" with no slots`);
  return out;
}

/** The perceptual knobs (rule 13). */
export const SAVE_POLICY_KNOBS = {
  often: "saves more or less often: scales the shortest gap between unforced autosaves (2 halves it; 0.5 doubles it). 1 is as tuned.",
  slots: "more or fewer manual slots (a count; 0 for autosave only, which also turns manual saving off).",
} as const;

/** Knob values. */
export interface SavePolicyKnobs {
  often?: number;
  slots?: number;
}

/** A policy turned by the knobs (a new row). With no knobs, equal to the input. */
export function tunePolicy(p: SavePolicy, k: SavePolicyKnobs = {}): SavePolicy {
  const often = k.often ?? 1;
  if (!(often > 0)) throw new Error(`save policies: often must be > 0 (got ${often})`);
  const out = structuredClone(p);
  if (often !== 1) out.minIntervalS = Math.round((out.minIntervalS / often) * 1000) / 1000;
  if (k.slots !== undefined) {
    if (!Number.isInteger(k.slots) || k.slots < 0) throw new Error(`save policies: slots must be a whole number ≥ 0 (got ${k.slots})`);
    out.slots = k.slots;
    if (k.slots === 0) out.manual = "none";
  }
  return out;
}
